import { faHouseTree, faSparkles } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTripContext } from '@/contexts/TripContext';
import { modeBlurb, tripModes } from '@/design-system/composite/TripModeMenu';
import { TripRow } from '@/design-system/composite/TripRow';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';

// Build the subtitle from the mode blurbs so this stays in sync with the
// copy living next to the mode picker. Oxford-comma joined, lowercased.
const blurbs = tripModes.map((m) => modeBlurb[m].toLowerCase());
const modeBlurbSentence =
  blurbs.length > 1
    ? `${blurbs.slice(0, -1).join(', ')}, and ${blurbs[blurbs.length - 1]}`
    : (blurbs[0] ?? '');

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string };

export interface EmptyStateProps {
  hasSearched?: boolean;
}

export const EmptyState = ({ hasSearched = false }: EmptyStateProps) => {
  const { trips, selectedTripId, selectTrip } = useTripContext();

  if (hasSearched) {
    return (
      <div className="flex flex-col items-center justify-center min-h-full w-full gap-5 px-8 py-8 text-center">
        <div className="text-6xl animate-pulse">🧭</div>
        <div className="space-y-2 max-w-sm">
          <h2 className="font-diary font-semibold text-3xl text-content-text">
            whoopsy doopsy, nothing here!! 🙈
          </h2>
          <p className="text-sm text-content-subtle leading-relaxed">
            ✨ maybe try exploring somewhere else? ✨
          </p>
        </div>
      </div>
    );
  }

  return (
    // Padding and gap tightened so the first saved trip in the list sits
    // above the fold on initial mobile load (was below the dvh boundary
    // due to pt-12 + 64px icon + gap-5 stacking ~140px above the trips
    // card). The wrapper still grows to content height; the outer
    // scroll container handles overflow when the trips list is long.
    <div className="flex flex-col items-center min-h-full w-full gap-3 px-8 pt-6 pb-6 text-center">
      <span className="motion-safe:animate-kawaii-sway origin-bottom inline-block">
        <FontAwesomeIcon
          icon={faHouseTree}
          style={
            {
              width: 52,
              height: 52,
              '--fa-primary-color': 'var(--color-primary-emphasized)',
              '--fa-secondary-color': 'var(--color-secondary-emphasized)',
              '--fa-secondary-opacity': '0.65',
            } as FAIconStyle
          }
        />
      </span>
      <div className="space-y-1.5 max-w-sm">
        <h2 className="font-diary font-semibold text-2xl text-content-text">where to next??</h2>
        <p className="text-sm text-content-subtle leading-relaxed flex items-center justify-center gap-2">
          <FontAwesomeIcon
            icon={faSparkles}
            style={
              {
                width: 14,
                height: 14,
                '--fa-primary-color': 'var(--color-secondary-emphasized)',
                '--fa-secondary-color': 'var(--color-primary-emphasized)',
                '--fa-secondary-opacity': '1',
              } as FAIconStyle
            }
          />
          <span>{modeBlurbSentence}</span>
          <FontAwesomeIcon
            icon={faSparkles}
            style={
              {
                width: 14,
                height: 14,
                transform: 'scaleX(-1)',
                '--fa-primary-color': 'var(--color-secondary-emphasized)',
                '--fa-secondary-color': 'var(--color-primary-emphasized)',
                '--fa-secondary-opacity': '1',
              } as FAIconStyle
            }
          />
        </p>
      </div>

      {trips.length > 0 ? (
        /* Wraps the trip list in the same white card treatment the trip
           selector menu uses (bg-white + content-border + rounded-2xl),
           so the two surfaces visually match. */
        <div className="w-full max-w-xs text-left bg-white border border-content-border rounded-xl overflow-hidden">
          <div className="p-2 space-y-0.5">
            <SectionEyebrow className="px-2 pt-1 pb-1">Trips</SectionEyebrow>
            {/* No inner max-height — the parent's overflow-y-auto handles
                scrolling, so the trips render at their natural height
                and contribute to the empty-state's total height. */}
            <div className="space-y-1">
              {trips.map((trip) => (
                <TripRow
                  key={trip.id}
                  trip={trip}
                  isSelected={selectedTripId === trip.id}
                  onSelect={() => selectTrip(trip.id)}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-xs text-content-disabled max-w-xs leading-relaxed flex items-center justify-center flex-wrap gap-1">
          use the <span className="font-medium text-content-subtle">trip selector</span> in the top
          right to create a trip and start planning
        </p>
      )}
    </div>
  );
};
